import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Paper, Typography, Divider, Chip } from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { API_ENDPOINTS, getData } from "../../services/apiService.js";
import { decryptId } from "../helper/idCodec.js";
import LoadingSpinner from "../LoadingContext";
import "./Truck-Owners.css";

function TruckOwnerDetails() {
    const navigate = useNavigate();
    const location = useLocation();

    const queryParams = new URLSearchParams(location.search);
    const encryptedId = queryParams.get("id");
    const ownerId = encryptedId ? decryptId(decodeURIComponent(encryptedId)) : "";

    const [owner, setOwner] = useState(null);
    const [counts, setCounts] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchOwnerDetails = async () => {
            if (!ownerId) {
                setLoading(false);
                setError("Invalid owner ID");
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const data = await getData(
                    `${API_ENDPOINTS.getTruckOwnerById}?owner_id=${ownerId}`
                );

                setOwner(data.result.owner_details);
                setCounts(data.result);
            } catch (err) {
                setError("Unable to fetch truck owner details.");
            } finally {
                setLoading(false);
            }
        };

        fetchOwnerDetails();
    }, [ownerId]);

    if (loading) {
        return <LoadingSpinner isLoading={true} />;
    }

    if (error) {
        return (
            <Typography p={3} color="error">
                {error}
            </Typography>
        );
    }

    if (!owner) {
        return <Typography p={3}>No owner data found.</Typography>;
    }

    const fullName = `${owner.first_name} ${owner.last_name}`;
    const totalTrucks = owner.Trucks.length;
    const activeTrips = owner.Trucks.filter((t) => t.tagged_to_trip).length;
    const totalDrivers = counts.drivers_count || 0;

    return (
        <Box p={3} className="truck-owner-details">
            {/* Header */}
            <Box display="flex" alignItems="center" gap={2} mb={3}>
                <img
                    src="/smart-vectors/Back.svg"
                    alt="back"
                    style={{ width: 32, height: 32, cursor: "pointer", display: "block" }}
                    onClick={() => navigate(`/app/truck-owners`)}
                />
                <Typography variant="h5" fontWeight={600}>
                    Truck Owner Details
                </Typography>
            </Box>

            {/* Row with 2 columns */}
            <div className="row" style={{ marginBottom: "16px" }}>
                {/* Left Column - Owner Info */}
                <div className="col-12 col-md-6 mb-3">
                    <Paper sx={{ p: 2, borderRadius: "16px", height: "100%" }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                            Owner Information
                        </Typography>
                        <Divider sx={{ mb: 2, mt: 1 }} />

                        <Typography variant="body2" color="text.secondary">
                            Owner Name
                        </Typography>
                        <Typography variant="body1" fontWeight={600} mb={2}>
                            {fullName}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                            Mobile Number
                        </Typography>
                        <Box display="flex" alignItems="center" mt={1} mb={2}>
                            <PhoneIcon sx={{ fontSize: 18, mr: 1 }} color="action" />
                            <Typography variant="body1" fontWeight={600}>
                                {owner.mobile_num}
                            </Typography>
                        </Box>

                        <Typography variant="body2" color="text.secondary">
                            Email
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                            {owner.email || "N/A"}
                        </Typography>
                    </Paper>
                </div>

                {/* Right Column - Stats */}
                <div className="col-12 col-md-6 mb-3">
                    <Paper
                        sx={{
                            p: 2,
                            borderRadius: "16px",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                        }}
                    >
                        <Box display="flex" gap={2} flexDirection="column">
                            <Paper
                                sx={{
                                    flex: 1,
                                    p: 2,
                                    textAlign: "center",
                                    bgcolor: "#E8F5E9",
                                }}
                            >
                                <Typography variant="h6" fontWeight="bold">
                                    {totalTrucks}
                                </Typography>
                                <Typography variant="body2">Total Trucks</Typography>
                            </Paper>
                            <Paper
                                sx={{
                                    flex: 1,
                                    p: 2,
                                    textAlign: "center",
                                    bgcolor: "#fdede3ff",
                                }}
                            >
                                <Typography variant="h6" fontWeight="bold" color="warning">
                                    {totalDrivers}
                                </Typography>
                                <Typography variant="body2">Total Drivers</Typography>
                            </Paper>
                            <Paper
                                sx={{
                                    flex: 1,
                                    p: 2,
                                    textAlign: "center",
                                    bgcolor: "#E3F2FD",
                                }}
                            >
                                <Typography variant="h6" fontWeight="bold" color="primary">
                                    {activeTrips}
                                </Typography>
                                <Typography variant="body2">Active Trips</Typography>
                            </Paper>
                        </Box>
                    </Paper>
                </div>
            </div>

            {/* Truck List */}
            <Paper sx={{ p: 2, borderRadius: "16px" }}>
                <Typography variant="subtitle1" fontWeight={600}>
                    Truck Information
                </Typography>
                <Divider sx={{ mb: 2, mt: 1 }} />

                <div className="truck-list" style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
                    {owner.Trucks.map((truck) => (
                        <Paper
                            key={truck.truck_id}
                            sx={{
                                flex: "0 0 calc(50% - 8px)", // 2 cards per row
                                p: 2,
                                borderRadius: "16px",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                bgcolor: "#f9f9f9",
                                marginBottom: "16px",
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={2}>
                                {truck.truck_pic ? (
                                    <img
                                        src={truck.truck_pic}
                                        alt={truck.truck_name}
                                        style={{
                                            width: 50,
                                            height: 40,
                                            objectFit: "cover",
                                            borderRadius: 6,
                                        }}
                                    />
                                ) : (
                                    <LocalShippingIcon color="primary" />
                                )}
                                <Box>
                                    <Typography fontWeight={600}>{truck.truck_name}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {truck.truck_no}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={300}>
                                        Capacity (Kgs) : {truck.capacity_inkgs} Kgs
                                    </Typography>
                                </Box>
                            </Box>
                            <Chip
                                className="chip-styles"
                                label={truck.tagged_to_trip ? "In Trip" : "Available"}
                                color={truck.tagged_to_trip ? "success" : "default"}
                            />
                        </Paper>
                    ))}
                </div>
            </Paper>
        </Box>
    );
}

export default TruckOwnerDetails;
